import { useStore } from '@nanostores/react';
import { Terminal as XTerm, type ITheme } from '@xterm/xterm';
import { useEffect, useRef, type ComponentProps } from 'react';
import { getTerminalTheme } from './theme';
import { createXTerm } from './xterm';
import { themeStore } from '~/lib/stores/theme';

export interface TerminalProps extends ComponentProps<'div'> {
  className?: string;
  readonly?: boolean;
  children: string;
  theme?: ITheme;

  // init-only prop
  webLinks?: boolean;
}

export interface TerminalRef {
  instance: XTerm;
}

export function Terminal({ readonly, webLinks, theme, children, ...terminalProps }: TerminalProps) {
  const ref = useRef<HTMLDivElement>(null);
  const terminalRef = useRef<XTerm>();
  const colorTheme = useStore(themeStore);

  useEffect(() => {
    const xterm = createXTerm({
      readonly: readonly ?? true,
      webLinks,
    });

    terminalRef.current = xterm.terminal;

    if (ref.current) {
      xterm.attach(ref.current);
    }

    return () => {
      xterm.dispose();
    };
  }, []);

  useEffect(() => {
    const terminal = terminalRef.current;

    if (!terminal) {
      return;
    }

    /**
     * We use `requestAnimationFrame` to ensure proper timing. This hook triggers
     * before the shared layout component's hook, which updates the theme attribute
     * on the html element. Without requestAnimationFrame, the CSS variables wouldn't
     * be updated yet, as the attribute change hasn't been processed.
     *
     * By using `requestAnimationFrame`, we allow the browser to complete the attribute
     * update and recalculate styles before applying the terminal theme, ensuring that
     * all necessary CSS variables are available and correctly set when we update the
     * terminal's appearance.
     */
    requestAnimationFrame(() => {
      terminal.options.theme = getTerminalTheme({
        // we render a transparent cursor in case the terminal is readonly
        ...(readonly && { cursor: '#00000000' }),
        ...theme,
      });
    });

    terminal.options.disableStdin = readonly;
  }, [colorTheme, theme, readonly]);

  useEffect(() => {
    const terminal = terminalRef.current;

    if (terminal && typeof children === 'string') {
      terminal.clear();
      terminal.write(children);
    }
  }, [children]);

  return <div translate="no" ref={ref} {...terminalProps} />;
}
